// Admin v2 — Bejelentkezés (Figma ADM_00): kártya logóval, Bejelentkezés/Regisztráció fül, Google-belépés.
import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Icon } from './icons';
import { Input, PasswordInput, Segmented } from './ui';
import logo from './dq-logo.svg';
import hero from './login-hero.webp';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function authMessage(code: string): string {
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'Az e-mail cím vagy a jelszó nem megfelelő.';
    case 'auth/too-many-requests':
      return 'Túl sok próbálkozás. Próbáld újra néhány perc múlva.';
    case 'auth/email-already-in-use':
      return 'Ezzel az e-mail címmel már van fiók.';
    case 'auth/weak-password':
      return 'A jelszó legalább 6 karakter legyen.';
    case 'auth/popup-closed-by-user':
      return '';
    case 'auth/network-request-failed':
      return 'Nincs internetkapcsolat.';
    default:
      return 'A belépés nem sikerült. Próbáld újra.';
  }
}

// A regisztráció az adminban egyelőre ki van kapcsolva: a fül látszik, de halványan, nem választható.
const REGISTRATION_DISABLED = true;

export default function AdminLogin() {
  const { signInWithEmail, signUpWithEmail, signInWithGoogle } = useAuth();
  const [mode, setMode] = useState<'in' | 'up'>('in');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsg('');
    if (!EMAIL_RE.test(email.trim())) return setMsg('Adj meg egy érvényes e-mail címet.');
    if (password.length < 6) return setMsg('A jelszó legalább 6 karakter legyen.');
    if (mode === 'up' && (!name.trim() || !username.trim())) return setMsg('Add meg a neved és a felhasználóneved.');
    setBusy(true);
    try {
      if (mode === 'in') await signInWithEmail(email.trim(), password);
      else if (!REGISTRATION_DISABLED) await signUpWithEmail(email.trim(), password, name.trim(), username.trim());
    } catch (err) {
      setMsg(authMessage((err as { code?: string }).code ?? ''));
    } finally {
      setBusy(false);
    }
  };

  const google = async () => {
    setMsg('');
    try {
      await signInWithGoogle();
    } catch (err) {
      setMsg(authMessage((err as { code?: string }).code ?? ''));
    }
  };

  return (
    <div className="relative min-h-screen bg-[#010306] overflow-hidden flex items-center justify-center p-4 lg:p-10">
      {/* Háttér: fényfoltok, darts-gyűrűk, repülési ív */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -left-[12%] top-[8%] w-[60vw] h-[60vw] max-w-[900px] max-h-[900px] rounded-full bg-[#00AFF2] opacity-[0.16] blur-[140px]" />
        <div className="absolute right-[-10%] bottom-[-20%] w-[50vw] h-[50vw] max-w-[760px] max-h-[760px] rounded-full bg-[#a855f7] opacity-[0.12] blur-[140px]" />
        <div className="absolute left-[38%] top-[-15%] w-[34vw] h-[34vw] max-w-[520px] max-h-[520px] rounded-full bg-[#2bc48a] opacity-[0.07] blur-[120px]" />
        {[1100, 820, 560, 320].map((s) => (
          <div
            key={s}
            className="absolute rounded-full border border-white/[0.05]"
            style={{ width: s, height: s, left: `calc(30% - ${s / 2}px)`, top: `calc(55% - ${s / 2}px)` }}
          />
        ))}
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 1440 900" preserveAspectRatio="none">
          <defs>
            <linearGradient id="dq-flight" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0" stopColor="#00AFF2" stopOpacity="0" />
              <stop offset="0.5" stopColor="#00AFF2" stopOpacity="0.45" />
              <stop offset="1" stopColor="#2bc48a" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d="M-40 640 C 320 380, 760 300, 1480 180" fill="none" stroke="url(#dq-flight)" strokeWidth="2" strokeDasharray="2 10" strokeLinecap="round" />
        </svg>
        <div
          className="absolute inset-0 opacity-[0.35]"
          style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)', backgroundSize: '28px 28px' }}
        />
      </div>

      {/* Mobilon: a mockup halványan a kártya mögött */}
      <img
        src={hero}
        alt=""
        aria-hidden
        className="lg:hidden pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[140vw] max-w-[900px] opacity-[0.18]"
      />

      <div className="relative z-10 w-full max-w-[1320px] flex items-center justify-center lg:justify-between gap-10">
        {/* Bal oldali hero (desktop) */}
        <div className="hidden lg:flex flex-1 min-w-0 flex-col max-w-[680px]">
          <span className="self-start inline-flex items-center h-8 px-4 rounded-full border border-[#00AFF2]/50 text-[#00AFF2] text-[12px] font-bold tracking-[0.18em] uppercase">
            Dartiqon · Admin
          </span>
          <h2 className="mt-5 text-[64px] xl:text-[76px] leading-[0.95] font-black italic uppercase text-white">
            A darts
            <br />
            <span className="bg-gradient-to-r from-[#00AFF2] to-[#2bc48a] bg-clip-text text-transparent pr-2">új</span>
            <br />
            otthona
          </h2>
          <p className="mt-4 text-[16px] text-[#B8B8C3]">Játékosok, klubok, táblák és meccsek – egy helyen kezelve.</p>
          <div
            className="relative -mt-6 -ml-10 w-[min(780px,118%)]"
            style={{ WebkitMaskImage: 'linear-gradient(to bottom, #000 72%, transparent 100%)', maskImage: 'linear-gradient(to bottom, #000 72%, transparent 100%)' }}
          >
            <img src={hero} alt="Dartiqon app képernyők" className="w-full h-auto select-none" draggable={false} />
          </div>
        </div>

      <form
        onSubmit={submit}
        noValidate
        className="w-full max-w-[440px] shrink-0 bg-[#15191E]/[0.85] backdrop-blur-xl border border-[#31383D] rounded-[24px] px-10 pt-10 pb-7 shadow-[0_30px_80px_-20px_rgba(0,175,242,0.25)]"
      >
        <div className="flex flex-col items-center">
          <img src={logo} alt="Dartiqon" className="w-[130px] h-auto" />
          <h1 className="mt-4 text-[28px] font-extrabold italic uppercase text-white">Üdvözlünk</h1>
          <p className="mt-2 text-[14px] text-[#B8B8C3]">
            {mode === 'in' ? 'Jelentkezz be a folytatáshoz' : 'Hozd létre a fiókodat'}
          </p>
        </div>

        <div className="mt-6 [&>div]:w-full [&>div>button]:flex-1">
          <Segmented
            value={mode}
            onChange={(m) => { setMode(m); setMsg(''); }}
            size="sm"
            options={[{ value: 'in', label: 'Bejelentkezés' }, { value: 'up', label: 'Regisztráció', disabled: REGISTRATION_DISABLED, title: REGISTRATION_DISABLED ? 'A regisztráció jelenleg nem elérhető' : undefined }]}
          />
        </div>

        <div className="mt-6 flex flex-col gap-4">
          {mode === 'up' && (
            <>
              <label className="block">
                <span className="block text-[13px] text-white mb-2">Teljes név</span>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="pl. Kiss János" autoComplete="name" />
              </label>
              <label className="block">
                <span className="block text-[13px] text-white mb-2">Felhasználónév</span>
                <Input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="pl. kissjanos" autoComplete="username" />
              </label>
            </>
          )}
          <label className="block">
            <span className="block text-[13px] text-white mb-2">E-mail cím</span>
            <Input icon="Envelope" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="pl. janos@example.com" autoComplete="email" />
          </label>
          <label className="block">
            <span className="block text-[13px] text-white mb-2">Jelszó</span>
            <PasswordInput
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Minimum 6 karakter"
              autoComplete={mode === 'in' ? 'current-password' : 'new-password'}
            />
          </label>
        </div>

        {msg && <div className="mt-4 text-[13px] text-[#ef4444] text-center" role="alert">{msg}</div>}

        <button
          type="submit"
          disabled={busy}
          className="mt-5 w-full h-[38px] rounded-full bg-[#00AFF2] text-white text-[14px] font-semibold hover:brightness-110 transition disabled:opacity-60"
        >
          {busy ? 'Folyamatban…' : mode === 'in' ? 'Bejelentkezés' : 'Regisztráció'}
        </button>

        <div className="mt-4 text-center text-[12px] text-[#8a8f99]">Vagy folytasd</div>

        <button
          type="button"
          onClick={google}
          className="mt-4 w-full h-[38px] rounded-full bg-[#1d232a] border border-[#31383D] text-white text-[14px] font-semibold inline-flex items-center justify-center gap-2 hover:border-[#00AFF2]/50 transition"
        >
          <Icon name="Globe" size={16} />
          Bejelentkezés Google-lal
        </button>

        <div className="mt-5 text-center text-[11px] text-[#8a8f99]">Dartiqon Admin</div>
      </form>
      </div>
    </div>
  );
}
